import { useMemo, useState } from 'react'
import { PaginationControls } from '@/components/import/PaginationControls'
import { AlertTriangle, Check, CircleDashed, Loader2, RefreshCw } from 'lucide-react'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@atrvd/saas-ui'
import { useProfile } from '@/hooks/useProfile'
import { useSageAccess } from '@/hooks/useSageAccess'
import { SageNotConnected } from '@/components/banking/SageNotConnected'
import { useFacilities } from '@/hooks/useBanking'
import {
  isActiveSync,
  useSageBankAccounts,
  useSageChecksAggregateByEntity,
  useSageSyncStatus,
  useSyncSageBankAccounts,
  useSyncSageChecks,
  useSyncSageFacilities,
  type SageSyncStatus,
} from '@/hooks/useSageIntegration'
import type { SageBankAccountRow, SageSyncStateRow } from '@/lib/db-tenant'
import { formatCurrency, formatDateTime } from '@dcp/shared/utils/formatters'
import { parseSageSyncWarnings } from '@dcp/shared/schemas/integrations'
import { hasPermission, isPlatformStaff } from '@dcp/shared'
import {
  deriveFacilityStatus,
  formatWarning,
  countBankAccountsByLocation,
  lastSeenAtByLocation,
  facilityNamesByLocation,
  type FacilityStatus,
  type CheckAggregate,
} from '@/lib/sage-utils'

/**
 * Page size for the two coverage tables on this page (FacilityCoverageTable
 * and BankAccountChecksTable). Both render purely from already-loaded
 * client-side data (no extra fetch per page), so the only point of paging
 * is visual digestibility past ~10 rows. PaginationControls auto-hides
 * when totalPages <= 1, so smaller tenants are visually unchanged.
 */
const SETTINGS_TABLE_PAGE_SIZE = 10

/**
 * Settings page for the Sage Intacct integration. The platform shares a
 * single Sage Intacct connection (credentials live in env vars on the
 * backend); this page surfaces the per-organization Bank Accounts and
 * Checks sync status and lets super_admin / admin trigger manual runs.
 * Facility ↔ Sage location mapping happens on the Settings → Facilities
 * page.
 */
export function SageIntacctPage() {
  const { data: profile } = useProfile()
  const { isEnabled: sageEnabled, organizationName: sageOrganizationName } = useSageAccess()
  const sync = useSageSyncStatus()
  const syncChecks = useSyncSageChecks()
  const syncBankAccounts = useSyncSageBankAccounts()
  const syncFacilities = useSyncSageFacilities()
  const {
    data: facilities = [],
    isLoading: facilitiesLoading,
    error: facilitiesError,
    // Full list (incl. inactive) — this feeds a name-LOOKUP map
    // (location_id → facility name) for a table that deliberately lists EVERY
    // Sage bank account, `missing_in_sage` rows included (see
    // useSageBankAccounts({ activeOnly: false }) below). Those rows outlive their
    // facility's deactivation, so an active-only list would degrade the Facility
    // column to "—" the moment a facility is switched off (PR #183 review #2a).
    // Same rule and same reason as IntacctBalanceTable. Row visibility is scoped
    // elsewhere; `activeFacilities` below is the selector-shaped view.
  } = useFacilities({ activeOnly: false })
  const {
    data: sageBankAccounts = [],
    isLoading: sageBankAccountsLoading,
    error: sageBankAccountsError,
  } = useSageBankAccounts({ activeOnly: false })
  // Server-side aggregate (RPC public.sage_checks_aggregate_by_entity).
  // Returns ≤ N rows where N is the count of distinct financial_entity
  // values in tenant.sage_checks — typically tens, never the full register.
  //
  // History: this used to be `useSageChecks()` + `aggregateChecksByEntity`
  // on the client. PostgREST's `max_rows = 1000` (supabase/config.toml)
  // silently truncated the list query on tenants with > 1k checks, and
  // the aggregator then under-counted everything because the missing
  // ~90% of rows weighed nothing on the count. The RPC moves the GROUP BY
  // into SQL so the result is independent of row limits.
  // See migration 20260601120000_sage_checks_aggregate_rpc.sql.
  const {
    data: checksAggregateRows = [],
    isLoading: sageChecksLoading,
    error: sageChecksError,
  } = useSageChecksAggregateByEntity()

  // Initial-load gate. The three queries above start in parallel — until at
  // least one of them resolves we render an empty page that looks like
  // "Sage is configured with no data" rather than "still loading". Match
  // the spinner pattern used by SettingsFacilitiesPage / IntegrationsPage so
  // the experience stays uniform across the settings cluster. Once any
  // query has data we render the page even if the others are still
  // in-flight — the rest of the file already handles per-section
  // empty / partial states gracefully.
  const isInitialLoading =
    facilitiesLoading &&
    sageBankAccountsLoading &&
    sageChecksLoading &&
    facilities.length === 0 &&
    sageBankAccounts.length === 0 &&
    checksAggregateRows.length === 0

  // Surface initial-load failures in one place — otherwise an RLS misconfig
  // or transient network error leaves the page rendering empty tables that
  // look like "no data" instead of "we couldn't fetch data".
  //
  // `sync.error` is intentionally NOT included: it's a polling hook that
  // re-runs every 3s, so a transient network blip would raise a misleading
  // destructive "Failed to load some data" banner even though all the data
  // is on screen. Polling errors get a softer warning alert below; the
  // destructive alert is reserved for data that failed to load at all.
  const loadErrors = [
    facilitiesError,
    sageBankAccountsError,
    sageChecksError,
  ].filter((err): err is Error => err instanceof Error)
  const pollError = sync.error instanceof Error ? sync.error : null

  /**
   * Who may trigger a Sage sync. Two independent paths, mirroring
   * `canInviteUsers` in @dcp/shared:
   *
   *  - platform staff keep the privilege on their system role alone, since
   *    their `product_role` may legitimately be null. The role list itself is
   *    NOT repeated here — `isPlatformStaff` owns it, so this check and the
   *    edge function's cannot drift apart (Presto PR #201 finding 4);
   *  - anyone holding the `manage_settings` product permission — i.e. the
   *    tenant's own Admin, whose system role is plain `'user'`.
   *
   * DCP-349: the second path was missing, so a Client Admin — the person who
   * owns this integration inside their organization — reached the page (the
   * route is gated only by RequireMFA) but found every Sync button replaced by
   * read-only status. Gating on the permission rather than on a hardcoded role
   * list also keeps this in step with the rest of Settings, which is routed
   * through `RequirePermission permission="manage_settings"`.
   */
  const canSync =
    isPlatformStaff(profile?.role) || hasPermission(profile?.productRole, 'manage_settings')

  // Per-type syncing flags. `sync.isSyncing` checks "any row is active" which
  // conflates the two sync types — keeping the per-type check via `isActiveSync`
  // on the matching row is enough and reads more honestly. `isActiveSync` treats
  // both a fresh 'pending' row (edge claimed the sync, task not yet started) and
  // a 'syncing' row as in-progress, and auto-frees once a 'pending' row goes
  // stale — so a never-started sync can't disable the button forever. The
  // mutation's `isPending` covers the moment between the trigger click and the
  // state-row appearing/flipping, so neither signal can be dropped.
  const isChecksSyncing =
    syncChecks.isPending || (sync.checks != null && isActiveSync(sync.checks))

  const isBankAccountsSyncing =
    syncBankAccounts.isPending ||
    (sync.bankAccounts != null && isActiveSync(sync.bankAccounts))

  // Facilities auto-import in-progress flag (DCP-262 AC#2). Same shape as the
  // bank-accounts flag: the mutation's `isPending` covers the click→row-appears
  // gap, and `isActiveSync` keeps the button disabled while the 'facilities'
  // sage_sync_state row is 'syncing' or freshly 'pending'.
  const isFacilitiesSyncing =
    syncFacilities.isPending ||
    (sync.facilities != null && isActiveSync(sync.facilities))

  // Group sage_bank_accounts by location_id so the coverage table can render
  // a count per facility in O(facilities + accounts) rather than nested loops.
  // Memoise the four per-collection aggregators so they only re-run when
  // their inputs (sageBankAccounts / sageChecks / facilities) actually
  // change — without `useMemo`, every unrelated re-render (tab switch,
  // toast firing, parent context update) would re-walk these collections
  // even though their inputs are stable. For mid-sized tenants (hundreds
  // of rows in sageChecks) this is the difference between a free no-op
  // and a hundred reduce iterations per keystroke.
  const bankAccountCountByLocation = useMemo(
    () => countBankAccountsByLocation(sageBankAccounts),
    [sageBankAccounts],
  )
  // Per-location max(last_seen_at) so the coverage table can render a "last
  // synced" timestamp for facilities that actually got Sage bank accounts back.
  const lastSeenByLocation = useMemo(
    () => lastSeenAtByLocation(sageBankAccounts),
    [sageBankAccounts],
  )

  // Only ACTIVE facilities take part in the Sage bank-accounts / checks sync
  // (DCP-262: facilities auto-imported from Sage arrive inactive and the admin
  // activates just the ones they use). The coverage table must show that same
  // active set — otherwise every not-yet-activated import shows up as noisy
  // "Not synced yet / 0 accounts" rows that will never sync. The Settings →
  // Facilities admin list still shows all facilities so they can be activated.
  const activeFacilities = useMemo(
    () => facilities.filter((f) => f.is_active),
    [facilities],
  )

  // Whether the bank-accounts sync has ever produced a result. Used to
  // distinguish "not synced yet" from "synced and unmatched".
  const bankAccountsSyncRanAtLeastOnce = !!sync.bankAccounts?.last_sync_at

  const bankAccountWarnings = parseSageSyncWarnings(sync.bankAccounts?.warnings)
  const checksWarnings = parseSageSyncWarnings(sync.checks?.warnings)
  const facilitiesWarnings = parseSageSyncWarnings(sync.facilities?.warnings)

  // Map aggregate rows into the `Map<financial_entity, CheckAggregate>` shape
  // that BankAccountChecksTable already consumes — keeping the table's
  // existing prop type unchanged keeps the diff focused on the data source.
  const checkAggByEntity = useMemo<Map<string, CheckAggregate>>(() => {
    const m = new Map<string, CheckAggregate>()
    for (const r of checksAggregateRows) {
      m.set(r.financial_entity, {
        total: r.total,
        outstanding: r.outstanding_count,
        outstandingAmount: Number(r.outstanding_amount),
      })
    }
    return m
  }, [checksAggregateRows])

  // Map facility name by sage_location_id so the Checks coverage table can show
  // "this Sage bank account belongs to <facility>" in one column.
  const facilityNameByLocation = useMemo(
    () => facilityNamesByLocation(facilities),
    [facilities],
  )

  // Orphan checks: records whose financial_entity is not present in the current
  // sage_bank_accounts catalog. Surfaced as a footer note so operators notice
  // when a bank account was removed from Sage but its checks linger here.
  //
  // Memoised for the same reason as the four aggregators above — without
  // useMemo, every unrelated re-render (tab switch, toast, parent context
  // update) would rebuild the Set and re-filter the check array. The Set
  // build is O(n) over bank accounts; the filter is O(n) over checks; for
  // tenants with hundreds of checks this is the difference between a free
  // no-op and a full sweep per render.
  const knownEntityIds = useMemo(
    () => new Set(sageBankAccounts.map((ba) => ba.financial_entity_id)),
    [sageBankAccounts],
  )
  // Sum total-check counts across aggregate rows whose financial_entity is
  // NOT in the current sage_bank_accounts catalog. Replaces the prior
  // client-side filter over raw sage_checks; the aggregate already contains
  // one row per entity with the per-entity total, so we just add them up.
  const orphanCheckCount = useMemo(
    () =>
      checksAggregateRows
        .filter((r) => !knownEntityIds.has(r.financial_entity))
        .reduce((sum, r) => sum + r.total, 0),
    [checksAggregateRows, knownEntityIds],
  )

  const checksSyncRanAtLeastOnce = !!sync.checks?.last_sync_at

  // DCP-368: one gate for the whole page, deliberately not per control.
  //
  // Sage credentials are platform-wide env vars, so this page used to render as
  // "connected" for every tenant — including one created for a different client,
  // whose syncs then pulled another client's Sage company into its schema.
  //
  // Placed BEFORE the loading check so the answer is immediate; the queries
  // above still fire, and their 403s are simply never rendered. Not worth
  // threading an `enabled` flag through six hooks to avoid.
  if (!sageEnabled) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sage Intacct</h1>
        </div>
        <SageNotConnected organizationName={sageOrganizationName} />
      </div>
    )
  }

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Sage Intacct</h1>
        <p className="text-muted-foreground">
          Sync the shared Sage Intacct connection catalogs and check register. Mapping happens per facility under Settings → Facilities.
        </p>
      </div>

      {loadErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load some data</AlertTitle>
          <AlertDescription>
            <ul className="ml-4 list-disc space-y-1">
              {loadErrors.map((err, idx) => (
                <li key={idx}>{err.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Softer warning when only the 3s polling refresh is failing —
          the already-loaded tables on screen are still authoritative,
          but the user should know the status panel is stale. */}
      {pollError && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Sync status updates paused</AlertTitle>
          <AlertDescription>
            Couldn't refresh the live sync status: {pollError.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Facilities auto-import (DCP-262) — placed at the TOP of the page so
          the very first thing an admin sees is the ability to pull their Sage
          locations into DCP. Mirrors the Bank Accounts / Checks cards: status
          row (Completed/Failed + Last run + Items synced) + Sync button with
          in-progress state. Imported facilities land on Settings → Facilities
          as Inactive rows with Source = "Sage Intacct". */}
      <Card>
        <CardHeader>
          <CardTitle>Facilities</CardTitle>
          <CardDescription>
            Imports all of your Sage Intacct locations into DCP as facilities. New facilities arrive inactive — rename, activate, and assign them to packs under Settings → Facilities.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SyncStatusRow row={sync.facilities} />

          {facilitiesWarnings.length > 0 && (
            <Alert variant="warning">
              <AlertTitle>Import warnings</AlertTitle>
              <AlertDescription>
                <ul className="ml-4 list-disc space-y-1">
                  {facilitiesWarnings.map((warning, idx) => (
                    <li key={idx}>{formatWarning(warning)}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {canSync ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncFacilities.mutate()}
              disabled={isFacilitiesSyncing}
            >
              {isFacilitiesSyncing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing…
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync Facilities
                </>
              )}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              Only administrators can trigger a sync.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bank Accounts</CardTitle>
          <CardDescription>
            Refreshes the catalog of Sage bank and savings accounts used to link DCP bank accounts to Sage checks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SyncStatusRow row={sync.bankAccounts} />

          {bankAccountWarnings.length > 0 && (
            <Alert variant="warning">
              <AlertTitle>Coverage warnings</AlertTitle>
              <AlertDescription>
                <ul className="ml-4 list-disc space-y-1">
                  {bankAccountWarnings.map((warning, idx) => (
                    <li key={idx}>{formatWarning(warning)}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <FacilityCoverageTable
            facilities={activeFacilities}
            bankAccountCountByLocation={bankAccountCountByLocation}
            lastSeenByLocation={lastSeenByLocation}
            syncRanAtLeastOnce={bankAccountsSyncRanAtLeastOnce}
            isLoading={facilitiesLoading}
          />

          {canSync ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncBankAccounts.mutate()}
              disabled={isBankAccountsSyncing}
            >
              {isBankAccountsSyncing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing…
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync Bank Accounts
                </>
              )}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              Only administrators can trigger a sync.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Checks</CardTitle>
          <CardDescription>
            Refreshes the AP check register pulled from Sage.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SyncStatusRow row={sync.checks} />

          {checksWarnings.length > 0 && (
            <Alert variant="warning">
              <AlertTitle>Coverage warnings</AlertTitle>
              <AlertDescription>
                <ul className="ml-4 list-disc space-y-1">
                  {checksWarnings.map((warning, idx) => (
                    <li key={idx}>{formatWarning(warning)}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <BankAccountChecksTable
            bankAccounts={sageBankAccounts}
            checkAggByEntity={checkAggByEntity}
            facilityNameByLocation={facilityNameByLocation}
            orphanCheckCount={orphanCheckCount}
            syncRanAtLeastOnce={checksSyncRanAtLeastOnce}
          />

          {canSync ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncChecks.mutate()}
              disabled={isChecksSyncing}
            >
              {isChecksSyncing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing…
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync Checks
                </>
              )}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              Only administrators can trigger a sync.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/** Minimal facility shape used by the coverage table — matches useFacilities(). */
interface CoverageFacility {
  id: string
  name: string
  sage_location_id: string | null
}

interface FacilityCoverageTableProps {
  facilities: CoverageFacility[]
  bankAccountCountByLocation: Map<string, number>
  lastSeenByLocation: Map<string, string>
  syncRanAtLeastOnce: boolean
  /**
   * Marks the facilities query as still in-flight. When true the component
   * renders a centred spinner instead of the "No facilities configured"
   * empty-state copy. Making the loading state explicit (rather than
   * relying on the parent's `isInitialLoading` gate) keeps the component
   * self-contained: a future caller in a dashboard widget can pass
   * `isLoading` directly and won't see the empty-state copy flash for a
   * tick on each mount. Optional + defaults to `false` so existing
   * callers that rely on a parent-level gate continue to work unchanged.
   */
  isLoading?: boolean
}

/**
 * Renders one row per facility showing how many Sage bank accounts are
 * currently mapped to its Sage location, when the location was last seen in
 * Sage, and a clear Status badge that tells the operator whether this facility
 * is wired up correctly to Sage.
 */
function FacilityCoverageTable({
  facilities,
  bankAccountCountByLocation,
  lastSeenByLocation,
  syncRanAtLeastOnce,
  isLoading = false,
}: FacilityCoverageTableProps) {
  // Client-side pagination: facilities are already loaded in full (typically
  // <100 rows), but past ~10 the table gets visually unwieldy. PaginationControls
  // itself bails when totalPages <= 1, so smaller tenants see no UI noise.
  const [page, setPage] = useState(0)
  const totalPages = Math.max(1, Math.ceil(facilities.length / SETTINGS_TABLE_PAGE_SIZE))
  // Clamp to last page if the underlying list shrinks below the current
  // page index (e.g. a facility deletion landed while we were on page 3).
  const safePage = Math.min(page, totalPages - 1)
  const pagedFacilities = facilities.slice(
    safePage * SETTINGS_TABLE_PAGE_SIZE,
    safePage * SETTINGS_TABLE_PAGE_SIZE + SETTINGS_TABLE_PAGE_SIZE,
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (facilities.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No active facilities. Activate a facility in Settings &rarr; Facilities
        for it to sync Sage bank accounts.
      </p>
    )
  }

  return (
    <div className="space-y-2">
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Facility name</TableHead>
            <TableHead>Sage Location ID</TableHead>
            <TableHead className="text-right">Bank accounts</TableHead>
            <TableHead>Last seen in Sage</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pagedFacilities.map((facility) => {
            const sageLocationId = facility.sage_location_id
            const count = sageLocationId
              ? bankAccountCountByLocation.get(sageLocationId) ?? 0
              : 0
            const lastSeen = sageLocationId
              ? lastSeenByLocation.get(sageLocationId)
              : undefined
            const status = deriveFacilityStatus({
              sageLocationId,
              count,
              syncRanAtLeastOnce,
            })
            const isMuted = status === 'not_configured' || status === 'pending'

            return (
              <TableRow key={facility.id}>
                <TableCell>
                  <FacilityStatusBadge status={status} />
                </TableCell>
                <TableCell className={isMuted ? 'text-muted-foreground' : 'font-medium'}>
                  {facility.name}
                </TableCell>
                <TableCell>
                  {sageLocationId ? (
                    <span className="font-mono text-sm">{sageLocationId}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <span className={count > 0 ? 'font-medium' : 'text-muted-foreground'}>
                    {count}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {lastSeen ? formatDateTime(lastSeen) : '—'}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
    {/* Controls render nothing when totalPages <= 1, so small tenants see no extra chrome. */}
    <PaginationControls page={safePage} totalPages={totalPages} onPageChange={setPage} />
    </div>
  )
}

/**
 * Visual badge for a facility's per-row sync status.
 */
function FacilityStatusBadge({ status }: { status: FacilityStatus }) {
  switch (status) {
    case 'synced':
      return (
        <Badge variant="success">
          <Check className="mr-1 h-3 w-3" />
          Synced
        </Badge>
      )
    case 'unmatched':
      return (
        <Badge variant="warning">
          <AlertTriangle className="mr-1 h-3 w-3" />
          No match
        </Badge>
      )
    case 'pending':
      return (
        <Badge variant="secondary">
          <CircleDashed className="mr-1 h-3 w-3" />
          Not synced yet
        </Badge>
      )
    case 'not_configured':
      return (
        <Badge variant="warning">
          <AlertTriangle className="mr-1 h-3 w-3" />
          Not configured
        </Badge>
      )
  }
}

interface BankAccountChecksTableProps {
  bankAccounts: SageBankAccountRow[]
  checkAggByEntity: Map<string, CheckAggregate>
  facilityNameByLocation: Map<string, string>
  /**
   * Count of checks in tenant.sage_checks whose financial_entity isn't in
   * the current sage_bank_accounts catalog. Derived server-side from the
   * aggregate (sum of `total` over orphan rows) — never the full register.
   */
  orphanCheckCount: number
  syncRanAtLeastOnce: boolean
}

/**
 * Renders one row per Sage bank account showing how many checks were pulled
 * for that account, of which how many are still outstanding, and the matched
 * DCP facility (via LOCATIONID). This is the per-account breakdown the
 * operator needs to verify each Sage account actually delivered its checks.
 */
function BankAccountChecksTable({
  bankAccounts,
  checkAggByEntity,
  facilityNameByLocation,
  orphanCheckCount,
  syncRanAtLeastOnce,
}: BankAccountChecksTableProps) {
  // Client-side pagination over the bank-accounts catalog. Same rationale
  // and same PAGE_SIZE as FacilityCoverageTable above — the data is already
  // loaded in full, so paging is purely cosmetic past ~10 rows.
  const [page, setPage] = useState(0)
  const totalPages = Math.max(1, Math.ceil(bankAccounts.length / SETTINGS_TABLE_PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  const pagedBankAccounts = bankAccounts.slice(
    safePage * SETTINGS_TABLE_PAGE_SIZE,
    safePage * SETTINGS_TABLE_PAGE_SIZE + SETTINGS_TABLE_PAGE_SIZE,
  )

  if (bankAccounts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No Sage bank accounts synced yet — run Sync Bank Accounts first.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bank account</TableHead>
              <TableHead>Account #</TableHead>
              <TableHead>Facility</TableHead>
              <TableHead className="text-right">Checks</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedBankAccounts.map((ba) => {
              const agg = checkAggByEntity.get(ba.financial_entity_id) ?? {
                total: 0,
                outstanding: 0,
                outstandingAmount: 0,
              }
              const facilityName = facilityNameByLocation.get(ba.location_id)
              const showZero = agg.total === 0
              const isInactive = ba.status === 'inactive' || ba.missing_in_sage

              return (
                <TableRow key={ba.financial_entity_id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className={isInactive ? 'text-muted-foreground' : 'font-medium'}>
                        {ba.name}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {ba.financial_entity_id}
                      </span>
                      {/* Surface inactive / missing-in-Sage state next to the name so it's obvious. */}
                      {ba.missing_in_sage && (
                        <Badge variant="warning" className="mt-1 w-fit">
                          missing in Sage
                        </Badge>
                      )}
                      {!ba.missing_in_sage && ba.status === 'inactive' && (
                        <Badge variant="secondary" className="mt-1 w-fit">
                          inactive
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {ba.bank_account_no ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {facilityName ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {showZero ? (
                      <span className="text-muted-foreground">
                        {syncRanAtLeastOnce ? '0' : '—'}
                      </span>
                    ) : (
                      <span className="font-medium">{agg.total}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {agg.outstanding > 0 ? (
                      <span className="font-medium">
                        {agg.outstanding}{' '}
                        <span className="text-muted-foreground text-xs">
                          ({formatCurrency(agg.outstandingAmount)})
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        {showZero && !syncRanAtLeastOnce ? '—' : '0'}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Controls render nothing when totalPages <= 1, so small tenants see no extra chrome. */}
      <PaginationControls page={safePage} totalPages={totalPages} onPageChange={setPage} />

      {/* Orphans: checks whose Sage bank account is no longer in our catalog. */}
      {orphanCheckCount > 0 && (
        <p className="text-xs text-muted-foreground">
          {orphanCheckCount} check{orphanCheckCount === 1 ? '' : 's'} reference Sage
          bank accounts no longer in your catalog (likely deleted in Sage after a previous sync).
        </p>
      )}
    </div>
  )
}

interface SyncStatusRowProps {
  row: SageSyncStateRow | null
}

/**
 * Renders the Sage sync status row: status badge, last-run timestamp,
 * items synced, and the last error (when in failed state).
 */
function SyncStatusRow({ row }: SyncStatusRowProps) {
  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium">Sync status</p>
        <StatusBadge status={(row?.sync_status as SageSyncStatus) ?? 'idle'} />
      </div>

      <dl className="space-y-1 text-sm">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Last run</dt>
          <dd>{row?.last_sync_at ? formatDateTime(row.last_sync_at) : '—'}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Items synced</dt>
          <dd>{row?.items_synced ?? 0}</dd>
        </div>
        {row?.last_error && row.sync_status === 'failed' && (
          <div className="rounded-md bg-destructive/10 p-2 text-sm text-destructive">
            {row.last_error}
          </div>
        )}
      </dl>
    </div>
  )
}

/**
 * Maps a sage_sync_state.sync_status value into a visual badge.
 * Idle / pending / syncing / completed / failed are the states the migration
 * enforces. 'pending' is the transient "claimed, waiting for the task to start"
 * state set by the edge function; it reads as in-progress just like 'syncing'.
 */
function StatusBadge({ status }: { status: SageSyncStatus }) {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="secondary">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Pending
        </Badge>
      )
    case 'syncing':
      return (
        <Badge variant="secondary">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Syncing
        </Badge>
      )
    case 'completed':
      return (
        <Badge variant="success">
          <Check className="mr-1 h-3 w-3" />
          Completed
        </Badge>
      )
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>
    default:
      return <Badge variant="secondary">Idle</Badge>
  }
}
